'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { createSupabaseBrowserClient } from '@/lib/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { TurnstileWidget } from '@/components/shared/TurnstileWidget';

export function RegisterForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const presetEmail = searchParams.get('email') ?? '';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(presetEmail);
  const [password, setPassword] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileEnabled = Boolean(
    process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!terms) {
      toast.error('Debes aceptar los términos y la política de privacidad.');
      return;
    }
    if (turnstileEnabled && !turnstileToken) {
      toast.error('Completa la verificación anti-bot antes de continuar.');
      return;
    }
    setLoading(true);

    if (turnstileEnabled && turnstileToken) {
      try {
        const verifyRes = await fetch('/api/turnstile/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: turnstileToken }),
        });
        if (!verifyRes.ok) {
          setLoading(false);
          toast.error('Verificación anti-bot fallida. Inténtalo de nuevo.');
          return;
        }
      } catch {
        setLoading(false);
        toast.error('No pudimos verificar la solicitud. Reintenta.');
        return;
      }
    }

    const supabase = createSupabaseBrowserClient();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/confirmar`,
        data: {
          full_name: fullName,
          marketing_consent: marketingConsent,
        },
      },
    });

    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }

    trackEvent('Register Completed', {
      marketing_consent: marketingConsent,
      auto_session: Boolean(data.session),
    });
    if (data.session) {
      toast.success('Cuenta creada. ¡Bienvenido a Oro Express Academy!');
      router.push('/dashboard');
      router.refresh();
    } else {
      toast.success(
        'Cuenta creada. Revisa tu email para confirmar (en dev: Mailhog en :8025).',
      );
      router.push('/login');
    }
  };

  return (
    <Card className="border-gold-500/20">
      <CardHeader className="space-y-2 text-center">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold-300">
          <Sparkles className="h-3 w-3" />
          100% gratuito
        </div>
        <CardTitle className="font-display text-3xl">Crea tu cuenta</CardTitle>
        <CardDescription>
          26 lecciones, certificado de Oro Express, sin tarjeta.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Nombre completo</Label>
            <Input
              id="full_name"
              required
              placeholder="Tu nombre"
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              placeholder="tu@email.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={8}
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <label className="flex cursor-pointer items-start gap-2 text-sm text-cream/70">
            <Checkbox
              checked={marketingConsent}
              onCheckedChange={(c) => setMarketingConsent(c === true)}
              className="mt-0.5"
            />
            <span>Quiero recibir novedades del curso (1-2 emails al mes).</span>
          </label>

          <label className="flex cursor-pointer items-start gap-2 text-sm text-cream/70">
            <Checkbox
              checked={terms}
              onCheckedChange={(c) => setTerms(c === true)}
              className="mt-0.5"
            />
            <span>
              Acepto los{' '}
              <Link
                href="/legal/terminos-uso"
                className="text-gold-300 hover:underline"
              >
                términos de uso
              </Link>{' '}
              y la{' '}
              <Link
                href="/legal/politica-privacidad"
                className="text-gold-300 hover:underline"
              >
                política de privacidad
              </Link>
              .
            </span>
          </label>

          {turnstileEnabled && (
            <div className="flex justify-center">
              <TurnstileWidget
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                action="register"
              />
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={loading || (turnstileEnabled && !turnstileToken)}
            className="w-full"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Crear mi cuenta gratis'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-cream/65">
          ¿Ya tienes cuenta?{' '}
          <Link href="/login" className="text-gold-300 hover:underline">
            Inicia sesión
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}
